import { SupportPermissions } from "@redotech/account-common/permissions";
import {
  createLazyContext,
  useLazyContextProvider,
  useRequiredContext,
} from "@redotech/react-util/context";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import {
  EffectApiClientContext,
  type EffectApiContext,
} from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import {
  SenderEmail,
  SenderEmailSetupStatus,
} from "@redotech/redo-support-settings-common/sender-email";
import { Effect } from "effect";
import { memo, ReactNode } from "react";
import { GmailIntegration, OutlookIntegration } from "../setting/email-info";

export interface EmailIntegrations {
  gmailIntegrations: GmailIntegration[];
  outlookIntegrations: OutlookIntegration[];
  customIntegrations: SenderEmail[];
}

export const EmailIntegrationsContext = createLazyContext<EmailIntegrations>();

async function loadEmailIntegrations(
  effectApi: EffectApiContext,
): Promise<EmailIntegrations> {
  const client = effectApi.getClient();
  const result = await effectApi.runPromise(
    client.integration
      .getTeamIntegrations({ urlParams: { limit: 100 } })
      .pipe(Effect.orDie),
  );

  const customIntegrations: SenderEmail[] = [];
  const gmailIntegrations: GmailIntegration[] = [];
  const outlookIntegrations: OutlookIntegration[] = [];

  for (const item of result.items) {
    switch (item.integrationType) {
      case "custom_email": {
        if (!item.channelData) {
          break;
        }
        customIntegrations.push({
          email: item.channelData.email,
          name: item.channelData.name,
          postmarkId: item.channelData.postmarkId,
          domainId: item.channelData.domainId,
          dkimHost: item.channelData.dkimHost,
          dkimText: item.channelData.dkimText,
          returnPathDomain: item.channelData.returnPathDomain,
          returnPathValue: item.channelData.returnPathValue ?? "",
          setupStatus:
            item.channelData.setupStatus === SenderEmailSetupStatus.CONNECTED
              ? SenderEmailSetupStatus.CONNECTED
              : SenderEmailSetupStatus.UNVERIFIED,
        });
        break;
      }
      case "gmail": {
        if (!item.channelData) {
          break;
        }
        gmailIntegrations.push({
          _id: String(item.id),
          email: item.channelData.email,
          name: item.channelData.gmailAccountName ?? "",
          image: item.channelData.gmailAccountImage ?? "",
          permissionsRevoked: item.permissionsRevoked,
        });
        break;
      }
      case "outlook": {
        if (!item.channelData) {
          break;
        }
        outlookIntegrations.push({
          _id: String(item.id),
          email: item.channelData.email,
          name: item.channelData.name ?? "",
          permissionsRevoked: item.permissionsRevoked,
        });
        break;
      }
      default:
        break;
    }
  }

  return { customIntegrations, gmailIntegrations, outlookIntegrations };
}

export const EmailIntegrationsService = memo(function EmailIntegrationsService({
  children,
}: {
  children: ReactNode | ReactNode[];
}) {
  const effectApi = useRequiredContext(EffectApiClientContext);
  const hasSupportPermission = useHasPermission(
    SupportPermissions.VIEW_SUPPORT,
  );
  const loadState = useLazyContextProvider(async () => {
    if (!hasSupportPermission) {
      return {
        customIntegrations: [],
        gmailIntegrations: [],
        outlookIntegrations: [],
      };
    }
    return loadEmailIntegrations(effectApi);
  });

  return (
    <EmailIntegrationsContext.internalContext.Provider value={loadState}>
      {children}
    </EmailIntegrationsContext.internalContext.Provider>
  );
});
